import { useId, useLayoutEffect, useRef } from 'react';
import { cn } from '@/lib/utils';
import { splitTextOnVariables } from '@/lib/urlVariables';

interface CodeFieldWithVariablesProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  variableValues: string[];
  maxLength: number;
  minRows?: number;
  disabled?: boolean;
  error?: string;
}

// Zone de code dont les variables sont surlignées comme les puces du lien de redirection.
// Le surlignage est un calque placé sous une zone de texte transparente, avec la même police,
// les mêmes marges et le même retour à la ligne : la saisie reste celle d'une zone de texte.
export function CodeFieldWithVariables({
  label,
  value,
  onChange,
  variableValues,
  maxLength,
  minRows = 6,
  disabled,
  error,
}: CodeFieldWithVariablesProps) {
  const id = useId();
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Hauteur ajustée au contenu : sans barre de défilement, le calque reste aligné sur le texte.
  useLayoutEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = 'auto';
    textarea.style.height = `${textarea.scrollHeight}px`;
  }, [value]);

  const sharedTextClassName =
    'm-0 w-full border-0 p-0 font-mono text-sm leading-6 whitespace-pre-wrap break-words';
  const isOver = value.length > maxLength;

  return (
    <div className="flex flex-col gap-1">
      <div
        className={cn(
          'relative rounded-lg border border-input px-4 pt-6 pb-2',
          disabled ? 'bg-subtle cursor-not-allowed' : 'bg-white',
        )}
      >
        <label htmlFor={id} className="absolute top-0.5 left-3 px-1 text-sm text-tertiary">
          {label}
        </label>
        <div className="relative">
          <div
            aria-hidden
            className={cn(
              sharedTextClassName,
              'pointer-events-none absolute inset-0 text-transparent',
            )}
          >
            {splitTextOnVariables(value, variableValues).map((segment, index) =>
              segment.isVariable ? (
                <mark
                  key={index}
                  className="rounded bg-decorative-1 text-transparent shadow-[0_0_0_2px_var(--color-decorative-1)]"
                >
                  {segment.text}
                </mark>
              ) : (
                <span key={index}>{segment.text}</span>
              ),
            )}
            {/* Une ligne finale vide doit compter, comme dans la zone de texte. */}
            {'\n'}
          </div>
          <textarea
            ref={textareaRef}
            id={id}
            value={value}
            rows={minRows}
            maxLength={maxLength}
            disabled={disabled}
            spellCheck={false}
            onChange={(event) => onChange(event.target.value)}
            className={cn(
              sharedTextClassName,
              'relative block resize-none overflow-hidden bg-transparent text-black focus:outline-none',
              disabled && 'cursor-not-allowed text-disable',
            )}
          />
        </div>
      </div>
      <div className="flex items-center justify-between">
        {error ? <p className="text-xs text-error">{error}</p> : <span />}
        <p className={cn('text-xs', isOver ? 'text-error' : 'text-secondary')}>
          {value.length}/{maxLength}
        </p>
      </div>
    </div>
  );
}
